import {
  FIRST_TOKEN_COST_PAGE,
  ROW_KEY_SEPARATOR,
  TOKEN_COST_FIGURE_NOTE_KEYS,
  TOKEN_COST_MAX_FRACTION_DIGITS,
  TOKEN_COST_MIN_FRACTION_DIGITS,
  type TTokenCostFigureNote,
  UNKNOWN_VALUE_PLACEHOLDER,
} from "../constants/token-usage-cost";
import type { ITokenUsageModelCost } from "../types/token-usage";
import { ETokenUsageTrendMetric } from "../types/token-usage";
import type {
  IAreAllTokenCountsUnknownProps,
  IBuildExportFileNameProps,
  IBuildTokenCostRowKeyProps,
  IDashCause,
  IDateRange,
  IFormatCurrencyProps,
  IFormatTokensOrUnknownProps,
  IFormatTrendAxisValueProps,
  IIsCachedChargedAtInputRateProps,
  IResolveDashCauseProps,
  IResolveTokenCostFigureNotesProps,
  IResolveTokenCostPageProps,
  ITokenCostPage,
} from "../types/token-usage-cost";
import { ETokenCostPeriod } from "../types/token-usage-cost";

const DAYS_BY_ROLLING_PERIOD: Partial<Record<ETokenCostPeriod, number>> = {
  [ETokenCostPeriod.LAST_7_DAYS]: 7,
  [ETokenCostPeriod.LAST_30_DAYS]: 30,
};

const DEFAULT_ROLLING_PERIOD_DAYS = 30;

// NOTE: Resolves a preset into concrete ISO from/to bounds passed to the API.
// ALL_TIME returns an empty range (no date filter). dateTo is always "now" so
// the current partial day is included.
export const getDateRangeForPeriod = (period: ETokenCostPeriod): IDateRange => {
  if (period === ETokenCostPeriod.ALL_TIME) {
    return {};
  }

  const now = new Date();
  const dateTo = now.toISOString();

  if (period === ETokenCostPeriod.CURRENT_MONTH) {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateFrom: start.toISOString(), dateTo };
  }

  const days = DAYS_BY_ROLLING_PERIOD[period] ?? DEFAULT_ROLLING_PERIOD_DAYS;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { dateFrom: start.toISOString(), dateTo };
};

// NOTE: Renders a monetary value. null (unpriced model) shows an em dash rather
// than a misleading $0. Allows up to 6 fraction digits so sub-cent per-model
// costs stay visible, matching the server's rounding.
export const formatCurrency = ({
  value,
  currency,
}: IFormatCurrencyProps): string => {
  if (value === null) {
    return UNKNOWN_VALUE_PLACEHOLDER;
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    // NOTE: narrowSymbol renders "$" instead of the locale-dependent "US$".
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: TOKEN_COST_MIN_FRACTION_DIGITS,
    maximumFractionDigits: TOKEN_COST_MAX_FRACTION_DIGITS,
  }).format(value);
};

export const formatTokens = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

// NOTE: NSAI-5207 — "every call this model was billed for came back without
// usage metadata", which is why its token AND cost figures all render a dash.
//
// Takes the two counts, not a model, so the per-model row AND the
// subtotal/totals shapes can all reach it — two of the three sites had
// re-implemented the expression inline, which is the exact drift this was
// extracted to prevent (#4933).
export const areAllTokenCountsUnknown = ({
  records,
  recordsMissingTokens,
}: IAreAllTokenCountsUnknownProps): boolean =>
  records > 0 && recordsMissingTokens === records;

// NOTE: NSAI-5210 — true when the cached tokens behind this figure WERE charged,
// at the full uncached input rate, because the model has an input rate but no
// cached-input one. The server folds them into inputCost in that case and leaves
// cachedInputCost null, which renders as a dash — so the reader sees cached
// tokens present, no cached cost, and an input cost that silently covers them.
//
// Takes the contributing MODELS rather than the three figures. Raised in review
// on #4933: on a subtotal or the totals row those three values come from
// DIFFERENT models — an unpriced model contributes the cached tokens, a priced
// one contributes the input cost — and the aggregate satisfies the test while no
// single model does. Every model that actually contributes cached tokens has to
// satisfy it individually; for a single row this reduces to exactly the old test.
export const isCachedChargedAtInputRate = ({
  models,
}: IIsCachedChargedAtInputRateProps): boolean => {
  const contributors = models.filter((model) => model.cachedInputTokens > 0);
  if (contributors.length === 0) return false;
  return contributors.every(
    (model) => model.cachedInputCost === null && model.inputCost !== null,
  );
};

export const formatTokensOrUnknown = ({
  value,
  areAllTokenCountsUnknown: isEveryTokenCountUnknown,
}: IFormatTokensOrUnknownProps): string =>
  isEveryTokenCountUnknown ? UNKNOWN_VALUE_PLACEHOLDER : formatTokens(value);

// NOTE: NSAI-5207 / NSAI-5210 / NSAI-5243 — which of the three reasons a blank
// cost figure carries, and the copy that says so. A blank cost is not
// self-explaining and the three causes are not interchangeable:
//
//   1. the provider never reported the usage, so the cost is unknown even though
//      a rate exists;
//   2. the model has an input rate but no cached-input rate, so these tokens
//      were charged — at the full input rate, inside Input cost;
//   3. no rate is configured for this dimension at all.
//
// 1 wins over 2 because when nothing about the usage is known there are no
// cached tokens to have charged.
export const resolveDashCause = ({
  areAllTokenCountsUnknown: isEveryTokenCountUnknown,
  isChargedAtInputRate,
}: IResolveDashCauseProps): IDashCause => {
  if (isEveryTokenCountUnknown) {
    return {
      testId: "unreported-usage",
      titleKey: "tokenCostUsage.unreportedUsageCellTitle",
    };
  }
  if (isChargedAtInputRate) {
    return {
      testId: "charged-at-input-rate",
      titleKey: "tokenCostUsage.chargedAtInputRateCellTitle",
    };
  }
  return {
    testId: "unpriced",
    titleKey: "tokenCostUsage.unpricedCellTitle",
  };
};

// NOTE: NSAI-5249 — which of the four "how to read these figures" rules apply to
// the period on screen. Each note is conditional: a rule about a state the
// period does not contain is noise that teaches a reader to stop reading the
// rest.
//
// The unpriced note is gated on a dash being ON SCREEN, not on
// incompletelyPricedModelCount — a model priced for input and output but with no
// CACHE rate is deliberately not "incompletely priced" (its total stays correct)
// yet still renders a dash. Rows whose usage went unreported are excluded: their
// dash already has a note, and it is the other one. So is a cached dash on a
// model that HAS an input rate — those tokens were charged, and counting that
// dash here captioned the same figure "no price is configured" two inches away.
export const resolveTokenCostFigureNotes = ({
  report,
  isOrganizationScope,
}: IResolveTokenCostFigureNotesProps): TTokenCostFigureNote[] => {
  // NOTE: NSAI-5775 — cache WRITES are a subset of input too, so they trip the
  // same "these columns are breakdowns, not addends" note that cache reads do.
  const hasSubsetTokens =
    report.totals.cachedInputTokens > 0 ||
    report.totals.cacheWriteInputTokens > 0 ||
    report.totals.reasoningTokens > 0;

  const isCachedDashCharged = (model: ITokenUsageModelCost): boolean =>
    isCachedChargedAtInputRate({ models: [model] });

  const hasUnpricedFigure = report.models.some(
    (model) =>
      !areAllTokenCountsUnknown(model) &&
      (model.inputCost === null ||
        (model.cachedInputCost === null && !isCachedDashCharged(model)) ||
        model.outputCost === null ||
        model.totalCost === null),
  );

  const hasChargedAtInputRateFigure = report.models.some(isCachedDashCharged);

  return [
    hasSubsetTokens ? TOKEN_COST_FIGURE_NOTE_KEYS.subset : null,
    hasUnpricedFigure ? TOKEN_COST_FIGURE_NOTE_KEYS.unpriced : null,
    hasChargedAtInputRateFigure
      ? TOKEN_COST_FIGURE_NOTE_KEYS.chargedAtInputRate
      : null,
    report.modelsWithMissingTokensCount > 0
      ? TOKEN_COST_FIGURE_NOTE_KEYS.unreportedUsage
      : null,
    // NOTE: NSAI-6266 — gated on ORG scope, and not on the data, unlike every
    // rule above it. It is not a fact about this period's rows: it holds for
    // every period, and the state it describes (a budget enforcing a narrower
    // figure) lives on a screen this report cannot see. A personal report has
    // no such budget to disagree with, so saying it there would send a reader
    // to a settings page that does not apply to them.
    isOrganizationScope
      ? TOKEN_COST_FIGURE_NOTE_KEYS.enforcedBudgetScope
      : null,
  ].filter((note): note is TTokenCostFigureNote => note !== null);
};

// NOTE: NSAI-5153 — the key has to be the SAME identity the server grouped on,
// or one API row can render as two children with one key. The server groups on
// model_id, falling back to (provider, model_name) for usage with no catalog
// row — and those rows carry a null modelUuid, so keying on modelName alone
// collapsed `text-embedding-3-small` under two providers onto a single key.
//
// Both modelUuid and modelName are nullable, so the index stays as the
// tiebreaker of last resort — reachable only when there is no identity left to
// key on (#4933). Joined on ROW_KEY_SEPARATOR rather than "-": a hyphen appears
// in real provider ids ("z-ai") and in nearly every model name, so ("a", "b-c")
// and ("a-b", "c") would both flatten to "a-b-c" (#4993).
//
// EITHER half of that pair missing sends the row to the tiebreaker, provider
// included. A bare model_name is not an identity: modelUuid is MAX(m.uuid) over
// a LEFT JOIN, so it is null whenever the catalog row behind a model_id is gone,
// and such rows group on model_id alone — two of them can therefore report the
// same model_name with provider null on both (the write path stores a null
// provider when neither the model entity nor the params carry one). Dropping the
// null half and keying on what was left handed both rows the same key, which is
// the collision this function exists to prevent.
export const buildTokenCostRowKey = ({
  model,
  groupKey,
  modelIndex,
}: IBuildTokenCostRowKeyProps): string => {
  if (model.modelUuid !== null) return model.modelUuid;
  if (model.modelName === null || model.provider === null) {
    return `${groupKey}${ROW_KEY_SEPARATOR}${modelIndex}`;
  }
  return [model.provider, model.modelName].join(ROW_KEY_SEPARATOR);
};

// NOTE: NSAI-5214 — how many fraction digits a cost axis needs to tell its own
// gridlines apart. A compact two-digit currency format collapses every tick of a
// sub-cent series to "$0": identical labels, and no way to read a value or a
// trend off the chart at all. That is the default view for a new user, a new
// project or any low-usage member.
//
// Derived from the axis MAXIMUM rather than per tick, so all labels share one
// precision; a per-tick precision would print "$0.000023" above "$0.01". Capped
// at TOKEN_COST_MAX_FRACTION_DIGITS, the precision the figures already render
// and the server already rounds to — beyond it the axis would claim more than
// the figures behind it.
export const resolveCostFractionDigits = (maxValue: number): number => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return TOKEN_COST_MIN_FRACTION_DIGITS;
  }
  if (maxValue >= 1) return TOKEN_COST_MIN_FRACTION_DIGITS;
  const leadingZeros = Math.ceil(-Math.log10(maxValue));
  return Math.min(
    TOKEN_COST_MAX_FRACTION_DIGITS,
    Math.max(TOKEN_COST_MIN_FRACTION_DIGITS, leadingZeros + 1),
  );
};

// NOTE: Axis formatter for the trends chart. Tokens stay compact — a y-axis of
// millions has to. Cost is compact only while it can be: above a cent the
// two-digit compact form is both correct and narrow, below it the axis widens to
// real precision instead of printing "$0" repeatedly (NSAI-5214). maxValue is
// the top of the plotted domain, so every tick on one axis is formatted alike.
export const formatTrendAxisValue = ({
  value,
  metric,
  currency,
  maxValue,
}: IFormatTrendAxisValueProps): string => {
  if (metric === ETokenUsageTrendMetric.COST) {
    const fractionDigits = resolveCostFractionDigits(maxValue);
    // NOTE: Compact notation caps at 2 fraction digits in every ICU locale, so
    // it cannot express a sub-cent tick — the two branches are a real either/or,
    // not a formatting preference.
    const isCompact = fractionDigits <= TOKEN_COST_MIN_FRACTION_DIGITS;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      ...(isCompact ? { notation: "compact" as const } : {}),
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  return formatTokens(value);
};

// NOTE: Filename characters that are unsafe or awkward across Windows, macOS and
// Linux, collapsed to a single "-" so an organization named "Acme / R&D" cannot
// produce a path segment or a name the platform silently rewrites.
const UNSAFE_FILE_NAME_CHARS = /[^\p{L}\p{N}._-]+/gu;
const EXPORT_FILE_NAME_BASE = "token-cost-usage";
const SCOPE_LABEL_MAX_LENGTH = 40;
const ISO_DATE_LENGTH = 10;

const toFileNamePart = (raw: string): string =>
  raw
    .trim()
    .replace(UNSAFE_FILE_NAME_CHARS, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SCOPE_LABEL_MAX_LENGTH);

// NOTE: Bucket starts and range bounds are ISO instants; only the calendar date
// belongs in a filename.
const toFileNameDate = (isoDate: string): string =>
  isoDate.slice(0, ISO_DATE_LENGTH);

// NOTE: Exports were named `token-cost-usage-<epoch ms>.csv`, so two downloads
// differed only by an integer and neither the period nor the scope could be
// recovered from a saved file (NSAI-5165). The name now carries both, e.g.
// `token-cost-usage_NodeShift_2026-07-21_2026-08-19.csv`. ALL_TIME has no
// bounds, so it is labelled instead of being left blank.
export const buildExportFileName = ({
  scopeLabel,
  format,
  allTimeLabel,
  dateFrom,
  dateTo,
}: IBuildExportFileNameProps): string => {
  const scopePart = toFileNamePart(scopeLabel);
  const rangePart =
    dateFrom && dateTo
      ? `${toFileNameDate(dateFrom)}_${toFileNameDate(dateTo)}`
      : toFileNamePart(allTimeLabel);

  const parts = [EXPORT_FILE_NAME_BASE, scopePart, rangePart].filter(Boolean);
  return `${parts.join("_")}.${format}`;
};

// NOTE: NSAI-5167 — slices the grouped model list into the visible page, and
// answers with the page it actually sliced.
//
// The clamp is the reason this is a function rather than two lines at the call
// site. `page` is UI state and the list it indexes is not, so anything that
// shortens the list can leave the stored page past the end — and the naive slice
// then renders an empty list under a control reading "51-60 of 40", a screen that
// looks like the period has no data. Callers reset the page on the changes they
// own (period, page size), so what is left to guard is a refetch shortening the
// list with no interaction at all. Deriving the page from the list on every
// render, rather than correcting the state afterwards, means there is no frame in
// which the control and the list disagree.
//
// An empty list still yields page 1 rather than page 0, so the control reads
// against a real page.
export const resolveTokenCostPage = ({
  groups,
  page,
  rowsPerPage,
}: IResolveTokenCostPageProps): ITokenCostPage => {
  const totalPages = Math.max(1, Math.ceil(groups.length / rowsPerPage));
  const currentPage = Math.min(
    Math.max(page, FIRST_TOKEN_COST_PAGE),
    totalPages,
  );
  const startIndex = (currentPage - FIRST_TOKEN_COST_PAGE) * rowsPerPage;
  return {
    currentPage,
    visibleGroups: groups.slice(startIndex, startIndex + rowsPerPage),
  };
};
